import React, { useEffect, useState } from 'react';
import { Table, Form } from 'react-bootstrap';
import Select from 'react-select';
import { postRequestOptions, getRequestOptions } from '../helpers/Fetchwrapper';
import { Card } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { formatWholeNumber } from '../helpers/GlobalHelper';
import { useLocation } from 'react-router-dom';

interface CustomerTransactionRow {
    sl_no: number;
    manager_name: string;
    area: string;
    number_of_customer: number;
    number_of_transaction: number;
}

interface CustomerTransactionData {
    rows: CustomerTransactionRow[];
    period_label: string;
}

const CustomerTransactionReport: React.FC = () => {
    const location = useLocation();
    const query = new URLSearchParams(location.search);

    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState<CustomerTransactionData | null>(null);
    const [projects, setProjects] = useState<{ label: string; value: string }[]>([]);
    const [project, setProject] = useState({ label: 'Select Project', value: '' });
    const [formData, setFormData] = useState({
        idProjects: query.get('idProjects') ?? '',
        fromDate: query.get('fromDate') ?? '',
        toDate: query.get('toDate') ?? '',
    });

    useEffect(() => {
        getProjects();
    }, []);

    useEffect(() => {
        if (projects.length > 0) {
            const projectObj = Number(query.get('idProjects'))
                ? projects.find((item) => item.value === Number(query.get('idProjects')))
                : projects[0];

            setProject({
                label: projectObj?.label ?? 'Select Project',
                value: projectObj?.value ?? '',
            });
            setFormData((prev) => ({
                ...prev,
                idProjects: projectObj ? String(projectObj.value) : '',
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects]);

    const getProjects = () => {
        fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                const projectOptions = resp.data.map((item: any) => ({
                    label: item.name,
                    value: item.id,
                }));
                setProjects(projectOptions);
            })
            .catch((error) => console.error('Error fetching projects:', error));
    };

    const handleProjectChange = (selected: any) => {
        setProject(selected);
        setFormData((prev) => ({
            ...prev,
            idProjects: selected ? String(selected.value) : '',
        }));
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleViewReport = async () => {
        if (!formData.idProjects) {
            toast.error('Please select a project', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        if (!formData.fromDate || !formData.toDate) {
            toast.error('Please select both From Date and To Date', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        if (formData.fromDate > formData.toDate) {
            toast.error('To Date must be after or equal to From Date', { position: 'bottom-right', autoClose: 4000 });
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL + 'mis-reports/customer-transaction-report',
                postRequestOptions(formData)
            );
            const data = await response.json();
            if (data.success) {
                setReportData(data.data);
            } else {
                setReportData(null);
                toast.error(data.message ?? 'Failed to load report', { position: 'bottom-right', autoClose: 4000 });
            }
        } catch (error) {
            console.error('Error submitting data:', error);
            toast.error('Failed to load report', { position: 'bottom-right', autoClose: 4000 });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Card className='mb-4'>
                <Card.Body>
                    <Card.Text>
                        <div className='row'>
                            <div className='col-md-3'>
                                <Form.Group className="mb-3" controlId="project">
                                    <Form.Label>Project<span className='required'></span></Form.Label>
                                    <Select
                                        value={project}
                                        onChange={handleProjectChange}
                                        options={projects}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                        isClearable={true}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="fromDate">
                                    <Form.Label>From Date<span className='required'></span></Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="fromDate"
                                        value={formData.fromDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="toDate">
                                    <Form.Label>To Date<span className='required'></span></Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="toDate"
                                        value={formData.toDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-3'>
                                <div className="form-control-sm ps-0 mt-7">
                                    <button className='btn btn-sm btn-primary p-3' onClick={handleViewReport} disabled={loading}>
                                        {loading && <span className="spinner-border spinner-border-sm"></span>}
                                        <span><i className='fas fa-eye'></i> View Report</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Card.Text>
                </Card.Body>
            </Card>

            <div className='row'>
                <div className='col-12'>
                    {reportData ? (
                        <>
                            <div className="mb-3">
                                <strong>Period:</strong> {reportData.period_label}
                            </div>
                            <Table responsive striped bordered hover size="sm">
                                <thead>
                                    <tr>
                                        <th>SL No</th>
                                        <th>Manager Name</th>
                                        <th>Area</th>
                                        <th className="text-end">Number of Customer</th>
                                        <th className="text-end">Number of Transaction</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportData.rows.map((row) => (
                                        <tr key={row.sl_no}>
                                            <td>{row.sl_no}</td>
                                            <td>{row.manager_name}</td>
                                            <td>{row.area}</td>
                                            <td className="text-end">{formatWholeNumber(row.number_of_customer)}</td>
                                            <td className="text-end">{formatWholeNumber(row.number_of_transaction)}</td>
                                        </tr>
                                    ))}
                                    {reportData.rows.length === 0 && (
                                        <tr>
                                            <td colSpan={5} className="text-center">No data available</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </>
                    ) : (
                        <div>No data available</div>
                    )}
                </div>
            </div>
        </>
    );
};

export default CustomerTransactionReport;
